import { A11yModule } from '@angular/cdk/a11y';
import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptors } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import localeDeCH from '@angular/common/locales/de-CH';
import localeGb from '@angular/common/locales/en-GB';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import localeSv from '@angular/common/locales/sv';
import { ErrorHandler, inject, NgModule } from '@angular/core';
import { BrowserModule, Title } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NavigationStart, Router, RouteReuseStrategy, RouterModule, ROUTES } from '@angular/router';
import { CalculationResultsApiConfiguration } from '@climateview/calculation-results-client';
import { EffectsModule } from '@ngrx/effects';
import { routerReducer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import * as Sentry from '@sentry/angular';
import { FormatIcu } from '@tolgee/format-icu';
import { DevTools, NgxTolgeeModule, Tolgee, TOLGEE_INSTANCE } from '@tolgee/ngx';
import {
  AbstractSecurityStorage,
  authInterceptor,
  AuthModule,
  DefaultLocalStorageService,
  LoginResponse,
  OidcSecurityService,
  StsConfigLoader,
} from 'angular-auth-oidc-client';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { enableMapSet } from 'immer';
import { CookieService } from 'ngx-cookie-service';
import { filter, take } from 'rxjs';
import { environment } from '../environments/environment';
import { ActionsApiModule } from './api/actions/actions-api.module';
import { AuditLogsApiConfiguration } from './api/audit-logs/audit-logs-api-configuration';
import { AuthorisationApiConfiguration } from './api/authorisation/authorisation-api-configuration';
import { Tenant } from './api/authorisation/models/api/tenant';
import { BoardsApiConfiguration } from './api/boards/boards-api-configuration';
import { BoardsApiModule } from './api/boards/boards-api.module';
import { CoreApiConfiguration } from './api/core/core-api-configuration';
import { FeatureFlagsService } from './api/core/services';
import { DataDefinitionsApiConfiguration } from './api/data-definitions/data-definitions-api-configuration';
import { EximApiConfiguration } from './api/exim/exim-api-configuration';
import { FilesApiConfiguration } from './api/files/files-api-configuration';
import { GuidingMetricsApiConfiguration } from './api/guiding-metrics/guiding-metrics-api-configuration';
import { PublishedBoardsApiConfiguration } from './api/published-boards/published-boards-api-configuration';
import { ServiceProvidersApiConfiguration } from './api/service-providers/service-providers-api-configuration';
import { APP_CONFIG } from './app-config.injection-token';
import { AppComponent } from './app.component';
import { FALLBACK_LANGUAGE, ICLEI_DOMAIN } from './app.constants';
import { CustomRouteReuseStrategy } from './app.route-reuse-strategy';
import { dashboardAppRoutes, fullAppRoutes } from './app.routes';
import { NAV_ROUTES } from './app.routes.constant';
import { AuthOverlayComponent } from './components/auth-overlay/auth-overlay.component';
import { getAuthenticationConfig } from './functions/authentication/get-authentication-config';
import { injectUntilDestroyed } from './functions/inject-functions/inject-until-destroyed';
import { FormatLegacy } from './functions/language/uistrings-tolgee';
import { canaryInterceptor } from './interceptors/canary.interceptor';
import { migrationStatusInterceptor } from './interceptors/migration-status.interceptor';
import { readOnlyInterceptor } from './interceptors/read-only-interceptor/read-only.interceptor';
import { RequestContextAdminViewInterceptor } from './interceptors/request-context-admin-view.interceptor';
import { requestContextInterceptor } from './interceptors/request-context.interceptor';
import { RetryInterceptor } from './interceptors/retry.interceptor';
import { AppConfig } from './model/app-config';
import { CookieDialogueComponent } from './modules/shared/components/cookie-dialogue/cookie-dialogue.component';
import { SharedModule } from './modules/shared/shared.module';
import { AnalyticsService } from './services/analytics.service';
import { AppConfigService } from './services/app-config.service';
import { CrossTabAuthService } from './services/cross-tab-auth/cross-tab-auth.service';
import { CustomDialogService } from './services/custom-dialog-service';
import { ErrorHandlerService } from './services/error-handler.service';
import { FallbackInterceptorService } from './services/fallback-interceptor.service';
import { LoggingService } from './services/logging.service';
import { MessageService } from './services/message.service';
import { TracingService } from './services/tracing-service/tracing.service';
import { WINDOW, WindowServiceProviders } from './services/window.service';
import { ActionsEffects } from './store/actions/actions.effects';
import { actionsReducer } from './store/actions/actions.reducer';
import { BoardEffects } from './store/board/board.effects';
import { boardReducer } from './store/board/board.reducer';
import { InventoriesEffects } from './store/inventories/inventories.effects';
import { inventoriesReducer } from './store/inventories/inventories.reducer';
import * as migrationStatusEffects from './store/migration-status/migration-status.effects';
import { migrationStatusReducer } from './store/migration-status/migration-status.reducer';
import * as publishedBoardsEffects from './store/published-boards/published-boards.effects';
import { publishedBoardsReducer } from './store/published-boards/published-boards.reducer';
import { RouterEffects } from './store/router/router.effects';
import { CustomRouterSerializer } from './store/router/router.serializer';
import { TenantsEffects } from './store/tenants/tenants.effects';
import { UserFacadeService } from './store/user/user-facade.service';
import { User } from './store/user/user.types';

enableMapSet();

registerLocaleData(localeGb);
registerLocaleData(localeSv);
registerLocaleData(localeDe);
registerLocaleData(localeFr);
registerLocaleData(localeEs);
registerLocaleData(localeNl);
registerLocaleData(localeDeCH);

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    SharedModule,
    ActionsApiModule,
    BoardsApiModule,
    BrowserModule,
    BrowserAnimationsModule,
    A11yModule,
    AngularSvgIconModule.forRoot(),
    RouterModule.forRoot([], { paramsInheritanceStrategy: 'always' }),
    CookieDialogueComponent,
    AuthOverlayComponent,
    StoreModule.forRoot(
      {
        board: boardReducer,
        migrationStatus: migrationStatusReducer,
        router: routerReducer,
        publishedBoards: publishedBoardsReducer,
        inventories: inventoriesReducer,
        actions: actionsReducer,
      },
      {}
    ),
    EffectsModule.forRoot([
      BoardEffects,
      RouterEffects,
      TenantsEffects,
      migrationStatusEffects,
      publishedBoardsEffects,
      InventoriesEffects,
      ActionsEffects,
    ]),
    StoreDevtoolsModule.instrument({ maxAge: 25, logOnly: environment.production, connectInZone: true }),
    StoreRouterConnectingModule.forRoot({ serializer: CustomRouterSerializer }),
    NgxTolgeeModule,
    AuthModule.forRoot({
      loader: {
        provide: StsConfigLoader,
        useFactory: getAuthenticationConfig,
        deps: [AppConfigService, WINDOW],
      },
    }),
  ],
  providers: [
    {
      provide: AppConfigService,
      useFactory: (appConfig: AppConfig) => {
        const isProduction = !!appConfig.auth.extraParams;
        const isIcleiDomain = inject<Window>(WINDOW).location.href.includes(ICLEI_DOMAIN);
        if (isProduction && !isIcleiDomain) {
          delete appConfig.auth.extraParams;
        }
        return new AppConfigService(appConfig);
      },
      deps: [APP_CONFIG],
    },
    {
      provide: ROUTES,
      multi: true,
      useFactory: (appConfig: AppConfig) => {
        if (!!appConfig.customHosting) {
          return dashboardAppRoutes(appConfig.customHosting);
        }
        return fullAppRoutes;
      },
      deps: [APP_CONFIG],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: FallbackInterceptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RequestContextAdminViewInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: RetryInterceptor,
      multi: true,
    },
    CrossTabAuthService,
    Title,
    AnalyticsService,
    CookieService,
    CustomDialogService,
    WindowServiceProviders,
    LoggingService,
    {
      provide: ErrorHandler,
      useClass: ErrorHandlerService,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    MessageService,
    { provide: RouteReuseStrategy, useClass: CustomRouteReuseStrategy },
    {
      provide: TOLGEE_INSTANCE,
      useFactory: () => {
        return Tolgee()
          .use(DevTools({}))
          .use(FormatLegacy())
          .use(FormatIcu())
          .init({
            language: 'en-GB',
            fallbackLanguage: FALLBACK_LANGUAGE,
            staticData: {
              'de-DE': () => import('../assets/i18n/de-DE.json'),
              'en-GB': () => import('../assets/i18n/en-GB.json'),
              'en-PA': () => import('../assets/i18n/en-PA.json'),
              'en-US': () => import('../assets/i18n/en-US.json'),
              'es-ES': () => import('../assets/i18n/es-ES.json'),
              'fr-FR': () => import('../assets/i18n/fr-FR.json'),
              'sv-PA': () => import('../assets/i18n/sv-PA.json'),
              'sv-SE': () => import('../assets/i18n/sv-SE.json'),
              'nl-NL': () => import('../assets/i18n/nl-NL.json'),
              'de-CH': () => import('../assets/i18n/de-CH.json'),
              'de-DE:dashboard': () => import('../assets/i18n/dashboard/de-DE.json'),
              'en-GB:dashboard': () => import('../assets/i18n/dashboard/en-GB.json'),
              'en-PA:dashboard': () => import('../assets/i18n/dashboard/en-PA.json'),
              'en-US:dashboard': () => import('../assets/i18n/dashboard/en-US.json'),
              'es-ES:dashboard': () => import('../assets/i18n/dashboard/es-ES.json'),
              'fr-FR:dashboard': () => import('../assets/i18n/dashboard/fr-FR.json'),
              'sv-PA:dashboard': () => import('../assets/i18n/dashboard/sv-PA.json'),
              'sv-SE:dashboard': () => import('../assets/i18n/dashboard/sv-SE.json'),
              'nl-NL:dashboard': () => import('../assets/i18n/dashboard/nl-NL.json'),
              'de-CH:dashboard': () => import('../assets/i18n/dashboard/de-CH.json'),
            },
            apiUrl: environment.tolgeeApiUrl,
            apiKey: environment.tolgeeApiKey,
            projectId: 1,
          });
      },
    },
    provideHttpClient(
      withInterceptors([
        migrationStatusInterceptor,
        canaryInterceptor,
        readOnlyInterceptor,
        authInterceptor(),
        requestContextInterceptor,
      ])
    ),
    { provide: AbstractSecurityStorage, useClass: DefaultLocalStorageService },
  ],
})
export class AppModule {
  private _untilDestroyed = injectUntilDestroyed();
  private _userFacade = inject(UserFacadeService);

  constructor(
    private _appConfigService: AppConfigService,
    private _boardsApiConfig: BoardsApiConfiguration,
    private _errorHandler: ErrorHandler,
    private _filesApiConfig: FilesApiConfiguration,
    private _coreApiConfig: CoreApiConfiguration,
    private _guidingMetricsApiConfig: GuidingMetricsApiConfiguration,
    private _auditLogsApiConfig: AuditLogsApiConfiguration,
    private _eximApiConfig: EximApiConfiguration,
    private _publishedBoardsApiConfig: PublishedBoardsApiConfiguration,
    private _featureFlagsService: FeatureFlagsService,
    private _authorisationApiConfig: AuthorisationApiConfiguration,
    private _dataDefinitionsApiConfig: DataDefinitionsApiConfiguration,
    private _serviceProvidersApiConfig: ServiceProvidersApiConfiguration,
    private _calculationResultsApiConfig: CalculationResultsApiConfiguration,
    private _oidcSecurityService: OidcSecurityService,
    private _router: Router
  ) {
    const proxyBaseUrl = this._appConfigService.appConfig.azure.proxy_base_url;
    this._boardsApiConfig.rootUrl = proxyBaseUrl + 'boards';
    this._filesApiConfig.rootUrl = proxyBaseUrl + 'files';
    this._coreApiConfig.rootUrl = proxyBaseUrl + 'core';
    this._guidingMetricsApiConfig.rootUrl = proxyBaseUrl + 'guiding-metrics/v1';
    this._auditLogsApiConfig.rootUrl = proxyBaseUrl + 'audit-logs/v1';
    this._eximApiConfig.rootUrl = proxyBaseUrl + 'exim/v1';
    this._publishedBoardsApiConfig.rootUrl = proxyBaseUrl + 'published-boards';
    this._featureFlagsService.rootUrl = proxyBaseUrl + 'feature-flags';
    this._authorisationApiConfig.rootUrl = proxyBaseUrl + 'auth';
    this._dataDefinitionsApiConfig.rootUrl = proxyBaseUrl + 'data/v1';
    this._serviceProvidersApiConfig.rootUrl = proxyBaseUrl + 'service-providers';
    this._calculationResultsApiConfig.rootUrl = proxyBaseUrl + 'calculation-results';

    (this._errorHandler as ErrorHandlerService).apiUrls = [
      this._boardsApiConfig.rootUrl,
      this._filesApiConfig.rootUrl,
      this._coreApiConfig.rootUrl,
      this._guidingMetricsApiConfig.rootUrl,
      this._auditLogsApiConfig.rootUrl,
      this._eximApiConfig.rootUrl,
      this._publishedBoardsApiConfig.rootUrl,
      this._featureFlagsService.rootUrl,
      this._authorisationApiConfig.rootUrl,
      this._dataDefinitionsApiConfig.rootUrl,
      this._serviceProvidersApiConfig.rootUrl,
    ];
    this._oidcSecurityService
      .checkAuth()
      .pipe(this._untilDestroyed())
      .subscribe((res: LoginResponse) => {
        console.debug('-- OIDC checkAuth response --\n', res);
        const { isAuthenticated } = res;
        if (isAuthenticated) {
          this._handleAuthenticatedUser();
        }
      });

    this._setupSentryAndHandleSignInLoopBug();
  }

  private _handleAuthenticatedUser() {
    this._userFacade.user$
      .pipe(
        filter((u): u is User => u !== undefined),
        take(1)
      )
      .subscribe((res) => {
        if (res && this._router.url.includes('sign-in')) {
          if (res.activeState?.activeTenant && res.activeState?.activeBoard) {
            this._router.navigateByUrl(
              NAV_ROUTES.BOARD_EDITOR_HOME_SCREEN(res.activeState.activeTenant, res.activeState.activeBoard)
            );
          } else {
            const tenants = res.serviceProviders.flatMap((s) => s.tenants ?? []);
            const firstTenantWithBoards = tenants.find((t): t is Required<Tenant> => !!t.boards?.length);
            if (!firstTenantWithBoards) {
              this._router.navigateByUrl(NAV_ROUTES.NO_MEMBERSHIP);
            } else {
              this._router.navigateByUrl(
                NAV_ROUTES.BOARD_EDITOR_HOME_SCREEN(firstTenantWithBoards.id, firstTenantWithBoards.boards[0].id)
              );
            }
          }
        }
      });
  }

  private _setupSentryAndHandleSignInLoopBug() {
    const tracingService = inject(TracingService);
    const window = inject<Window>(WINDOW);
    const { origin } = window.location;
    let loopCount = Number(sessionStorage.getItem('cv-loop-count')) ?? 0;
    this._router.events.pipe(this._untilDestroyed()).subscribe((event) => {
      if (event instanceof NavigationStart) {
        if (event.url === '/') {
          loopCount++;
        } else {
          loopCount = 0;
        }
        sessionStorage.setItem('cv-loop-count', loopCount.toString());

        const startedReplay = sessionStorage.getItem('cv-started-replay') === 'true';
        if (loopCount >= 5) {
          if (!startedReplay) {
            Sentry.init({
              dsn: 'https://55e278f0139271ea714601454b52a899@o4509751923376128.ingest.de.sentry.io/4509751944020048',
              // This initializes Replay without starting any session
              replaysSessionSampleRate: 0,
              replaysOnErrorSampleRate: 0,
              integrations: [Sentry.replayIntegration()],
            });
            Sentry.getReplay()?.start();
            sessionStorage.setItem('cv-started-replay', 'true');
          }
          sessionStorage.removeItem('cv-loop-count');
          loopCount = 0;
          const { endSessionEndpoint } = this._appConfigService.appConfig.auth;
          const clientId = getAuthenticationConfig(this._appConfigService, window)['passedConfigs'].clientId;
          tracingService.trace('oidc-end-session', () => {
            window.location.href = `${endSessionEndpoint}?client_id=${clientId}&post_logout_redirect_uri=${origin}`;
          });
        }
      }
    });
  }
}
